/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { FC, ReactNode, useState, useEffect, Fragment } from 'react';
import { Box, Divider, Paper, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import routes from '../../../routes';
import colors from '../../../themes/main-colors';

import useContentMgmt, { ProductData } from '../../../hooks/useContentMgmt';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import {
  ApplicationEvSectionType,
  SectionEvAccountInfo,
  HomeType,
  TimeFrame,
  PanelLocation,
  PanelDistance,
  PanelSideLocation,
  DoubleBreakerAvailable,
  MainBreakerAmperage,
  ChargerInstallLocation,
  SectionEvRebates,
  SectionEvChargerSelection,
  SectionEvPanelInfo,
  SectionEvInstall,
  RebateSmartCharging,
  DayOfWeek,
} from '../../../__generated__/pgeplus-types';
import { titleCase } from '../../../util/string-utils';
import {
  displayPhoneNumberFormat,
  toCurrencyString,
} from '../../../util/format';
import PGEPlusButtons from '../../pge-plus-common/PGEPlusButton';
import { navigate } from 'gatsby';

interface SummaryProps {
  children?: ReactNode;
  path: string;
}

export const ViewSubmittedEVApplicationSummary: FC<SummaryProps> = props => {
  const {
    formState: { application },
  } = useEVChargerFormState();

  const [evChargerProductList, setEvChargerProductList] = useState<
    ProductData[]
  >([]);

  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_SUMMARY);

  const { content: contentContactInfo, getProductList } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_CONTACT_INFO,
  );
  const { content: contentInstallation } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLATION_LOCATION,
  );
  const { content: contentUploadPhotos } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS,
  );

  const theme = useTheme();

  const selectCharger = (application?.details?.find(
    section =>
      section.sectionType === ApplicationEvSectionType.EvChargerSelection,
  ) as SectionEvChargerSelection)?.data;

  useEffect(() => {
    const fetchProductList = async (productListEntryId: string) => {
      const productList = await getProductList({
        productListEntryId,
      });
      setEvChargerProductList(productList);
      //console.log('productList', productList);
    };
    fetchProductList(
      selectCharger.suppliedByCustomer
        ? microcopyGroupIds.PGE_PLUS_CONFIRM_YOUR_CHARGER_PRODUCT_LIST
        : microcopyGroupIds.PGE_PLUS_SELECT_CHARGER_PRODUCT_LIST,
    ).catch(err => {
      console.log('fetchProductList error: ', err);
    });
  }, []);

  const accountInfo = (application?.details?.find(
    section => section.sectionType === ApplicationEvSectionType.EvAccountInfo,
  ) as SectionEvAccountInfo)?.data;

  const rebateInfo = (application?.details?.find(
    section => section.sectionType === ApplicationEvSectionType.EvRebates,
  ) as SectionEvRebates)?.data;

  const installation = (application?.details?.find(
    section => section.sectionType === ApplicationEvSectionType.EvInstall,
  ) as SectionEvInstall)?.data;

  const panelInfo = (application?.details?.find(
    section => section.sectionType === ApplicationEvSectionType.EvPanelInfo,
  ) as SectionEvPanelInfo)?.data;

  const RELATIONSHIP_TYPES = {
    parent: contentContactInfo.get('RELATIONSHIP_PARENT'),
    sibling: contentContactInfo.get('RELATIONSHIP_SIBLING'),
    spouse: contentContactInfo.get('RELATIONSHIP_SPOUSE'),
    partner: contentContactInfo.get('RELATIONSHIP_PARTNER'),
    friend: contentContactInfo.get('RELATIONSHIP_FRIEND'),
    other: contentContactInfo.get('RELATIONSHIP_OTHER'),
  };

  const HOME_TYPES = {
    [HomeType.Detached]: contentContactInfo.get('DETACHED'),
    [HomeType.Townhouse]: contentContactInfo.get('TOWNHOUSE'),
    [HomeType.Duplex]: contentContactInfo.get('DUPLEX'),
    [HomeType.Triplex]: contentContactInfo.get('TRIPLEX'),
    [HomeType.Apartment]: contentContactInfo.get('APARTMENT'),
    [HomeType.Condo]: contentContactInfo.get('CONDO'),
  };

  const TIMEFRAME = {
    [TimeFrame.Morning]: contentInstallation.get('MORNING_8AM_TO_12PM'),
    [TimeFrame.Afternoon]: contentInstallation.get('AFTERNOON_12PM_TO_5PM'),
    [TimeFrame.Any]: contentInstallation.get('NO_PREFERENCE'),
  };

  const PANEL_LOCATION = {
    [PanelLocation.GarageAttached]: contentInstallation.get('ATTACHED_GARAGE'),
    [PanelLocation.Basement]: contentInstallation.get('BASEMENT'),
    [PanelLocation.Inside]: contentInstallation.get('INSIDE_THE_HOME'),
    [PanelLocation.Outside]: contentInstallation.get('OUTSIDE_THE_HOME'),
    [PanelLocation.NotSure]: contentInstallation.get('NOT_SURE'),
    [PanelLocation.Other]: contentInstallation.get('OTHER'),
  };

  const CHARGER_INSTALL_LOCATION = {
    [ChargerInstallLocation.Garage]: contentInstallation.get('GARAGE'),
    [ChargerInstallLocation.Outside]: contentInstallation.get(
      'OUTSIDE_THE_HOME_PLAIN',
    ),
  };

  const DISTANCE_TO_PANEL = {
    [PanelDistance.Lt_5]: contentInstallation.get('5_FEET_OR_LESS'),
    [PanelDistance.Btw_6_30]: contentInstallation.get('6_TO_30_FEET'),
    [PanelDistance.Gt_30]: contentInstallation.get('MORE_THAN_30_FEET'),
  };

  const PANEL_SIDE_LOCATION = {
    [PanelSideLocation.Left]: contentInstallation.get('LEFT_SIDE'),
    [PanelSideLocation.BackWall]: contentInstallation.get('BACK_SIDE'),
    [PanelSideLocation.Right]: contentInstallation.get('RIGHT_SIDE'),
  };

  const DAY_OF_THE_WEEK = {
    [DayOfWeek.Monday]: contentInstallation.get('REUSABLE_DAY_MONDAY'),
    [DayOfWeek.Tuesday]: contentInstallation.get('REUSABLE_DAY_TUESDAY'),
    [DayOfWeek.Wednesday]: contentInstallation.get('REUSABLE_DAY_WEDNESDAY'),
    [DayOfWeek.Thursday]: contentInstallation.get('REUSABLE_DAY_THURSDAY'),
    [DayOfWeek.Friday]: contentInstallation.get('REUSABLE_DAY_FRIDAY'),
  };

  const MAIN_BREAKER_AMPS = {
    [MainBreakerAmperage.Unknown]: contentUploadPhotos.get('IM_NOT_SURE'),
    [MainBreakerAmperage.Amps_200]: contentUploadPhotos.get('200_AMPS'),
    [MainBreakerAmperage.Amps_150]: contentUploadPhotos.get('150_AMPS'),
    [MainBreakerAmperage.Amps_125]: contentUploadPhotos.get('125_AMPS'),
    [MainBreakerAmperage.Amps_100]: contentUploadPhotos.get('100_AMPS'),
    [MainBreakerAmperage.NoMainBreaker]: contentUploadPhotos.get(
      'NO_MAIN_BREAKER',
    ),
  };

  const DOUBLE_BREAKER_OPTIONS = {
    [DoubleBreakerAvailable.Unknown]: contentUploadPhotos.get('IM_NOT_SURE'),
    [DoubleBreakerAvailable.Yes]: contentUploadPhotos.get('REUSABLE_YES'),
    [DoubleBreakerAvailable.No]: contentUploadPhotos.get('REUSABLE_NO'),
  };
  const rebate = rebateInfo?.rebateEligibility?.evRebate as RebateSmartCharging;

  const isGarageInstallation =
    installation?.panelLocation?.location === PanelLocation.GarageAttached ||
    installation?.chargerInstallPreference?.installLocation ===
      ChargerInstallLocation.Garage;

  const summary = [
    {
      sectionTitle: content.get('CUSTOMER_INFORMATION'),
      items: [
        {
          label: content.get('CONTACT_INFO')!,
          values: [
            accountInfo?.useAccountContact
              ? `${accountInfo?.accountContact?.firstName} ${accountInfo?.accountContact?.middleName} ${accountInfo?.accountContact?.lastName}`
              : `${accountInfo?.preferredContact?.firstName} ${accountInfo?.preferredContact?.middleName} ${accountInfo?.preferredContact?.lastName}`,
            accountInfo?.useAccountContact
              ? accountInfo?.accountContact?.phoneNumber
              : displayPhoneNumberFormat(
                  accountInfo?.preferredContact?.phoneNumber,
                ),
            accountInfo?.useAccountContact
              ? accountInfo?.accountContact?.emailAddress
              : accountInfo?.preferredContact?.emailAddress,
            !accountInfo?.useAccountContact &&
            accountInfo?.preferredContact?.relationship
              ? RELATIONSHIP_TYPES[
                  accountInfo?.preferredContact
                    ?.relationship as keyof typeof RELATIONSHIP_TYPES
                ]
              : '',
          ],
        },
        {
          label: content.get('ADDRESS')!,
          values: [accountInfo?.address?.addressLine1],
        },
        {
          label: content.get('ARE_YOU_HOMEOWNER'),
          values: [
            accountInfo?.isOwner
              ? contentContactInfo.get('REUSABLE_YES')
              : contentContactInfo.get('REUSABLE_NO'),
          ],
        },
        {
          label: content.get('TYPE_OF_HOME'),
          values: [HOME_TYPES[accountInfo?.homeType!] || ''],
        },
      ],
    },
    {
      sectionTitle: content.get('REBATE_ELIGIBILITY_TITLE'),
      items: [
        {
          label: content.get('INCOME_ELIGIBILITY'),
          values: [
            rebateInfo?.iqdb?.houseHoldSize ?? 'N/A',
            rebateInfo?.iqdb?.grossIncome
              ? `$${toCurrencyString(
                  Number(rebateInfo?.iqdb?.grossIncome),
                  true,
                  true,
                )}`
              : 'NA',
          ],
        },
        {
          label: content.get('WIFI'),
          values: [
            rebateInfo?.smartCharging?.wifiAvailable
              ? contentContactInfo.get('REUSABLE_YES')
              : contentContactInfo.get('REUSABLE_NO'),
          ],
        },
        {
          label: content.get('ESTIMATED_REBATES'),
          values: [
            rebate?.charger
              ? `$${toCurrencyString(Number(rebate?.charger), true, true)}`
              : rebate?.amount
              ? `$${toCurrencyString(Number(rebate?.amount), true, true)}`
              : 'NA',
          ],
        },
      ],
    },
    {
      sectionTitle: content.get('CONFIRM_YOUR_CHARGER'),
      items: [
        {
          label: content.get('CHARGER_SELECTION'),
          values: [
            selectCharger.productId
              ? evChargerProductList.filter(
                  charger => charger.id === selectCharger.productId,
                )?.[0]?.entryName
              : 'N/A',
          ],
        },
      ],
    },
    {
      sectionTitle: content.get('REUSABLE_INSTALLATION')!,
      items: [
        {
          label: content.get('HOW_SOON_NEED_INSTALLATION')!,
          values: [installation.schedulePreference?.urgency],
        },
        {
          label: content.get('WHEN_YOU_NEED_YOUR_CHARGER_INSTALLED')!,
          values: [
            installation?.schedulePreference?.dayOfWeek
              ?.map(day => DAY_OF_THE_WEEK[day as keyof typeof DAY_OF_THE_WEEK])
              .join(', '),
            installation?.schedulePreference?.preferredServiceTime?.join(', '),
          ],
        },
        {
          label: content.get('ELECTRICAL_PANEL_LOCATION_TITLE')!,
          values: [
            installation?.panelLocation?.location
              ? PANEL_LOCATION[installation?.panelLocation?.location]
              : '',
          ],
        },
        ...(installation?.panelLocation?.location === PanelLocation.Other
          ? [
              {
                label: contentInstallation.get('OTHER_COMMENTS_FIELD_LABEL')!,
                values: [
                  installation?.panelLocation?.comment
                    ? installation?.panelLocation?.comment
                    : '',
                ],
              },
            ]
          : []),
        ...(installation?.panelLocation?.location === PanelLocation.Basement ||
        installation?.panelLocation?.location === PanelLocation.Inside ||
        installation?.panelLocation?.location === PanelLocation.NotSure ||
        installation?.panelLocation?.location === PanelLocation.Other
          ? [
              {
                label: contentInstallation.get('CHARGER_IN_GARAGE_OR_OUTSIDE')!,
                values: [
                  installation.chargerInstallPreference?.installLocation
                    ? CHARGER_INSTALL_LOCATION[
                        installation.chargerInstallPreference?.installLocation
                      ]
                    : '',
                ],
              },
            ]
          : []),
        ...(!installation?.chargerInstallPreference
          ?.installerRecommendPanelDistance
          ? [
              {
                label: content.get('DISTANCE_FROM_GARAGE_TO_PANEL')!,
                values: [
                  installation?.chargerInstallPreference?.distanceToPanel
                    ? DISTANCE_TO_PANEL[
                        installation?.chargerInstallPreference?.distanceToPanel
                      ]
                    : '',
                ],
              },
            ]
          : [
              {
                label: content.get('DISTANCE_FROM_GARAGE_TO_PANEL')!,
                values: [
                  contentInstallation.get('PREFER_INSTALLER_RECOMMENDATION'),
                ],
              },
            ]),
        ...(!installation.chargerInstallPreference
          ?.installerRecommendPanelDistance && isGarageInstallation
          ? [
              {
                label: content.get('PANEL_LOCATION')!,
                values: [
                  installation?.chargerInstallPreference?.panelSideLocation
                    ? PANEL_SIDE_LOCATION[
                        installation?.chargerInstallPreference
                          ?.panelSideLocation
                      ]
                    : '',
                ],
              },
            ]
          : []),
      ],
    },
    {
      sectionTitle: content.get('UPLOAD_YOUR_PHOTOS')!,
      items: [
        {
          label: content.get('PHOTOS_PROVIDED')!,
          values: [
            panelInfo?.panelImage,
            panelInfo?.amperageImage,
            panelInfo?.panelLocationImage,
          ],
        },
        {
          label: contentUploadPhotos
            .get('YOUR_CHARGER_LOCATED_QUESTION')
            ?.slice(3)!,
          values: [
            panelInfo?.installDistance?.extendedInstallDistance
              ? panelInfo?.installDistance?.extendedInstallDistanceImage
              : contentUploadPhotos.get('REUSABLE_YES'),
          ],
        },
        {
          label: content.get('AMPS_ON_MAIN_BREAKER')!,
          values: [
            panelInfo?.breakerInfo?.mainBreakerAmperage
              ? MAIN_BREAKER_AMPS[panelInfo?.breakerInfo?.mainBreakerAmperage]
              : '',
          ],
        },
        {
          label: content.get('TWO_SLOTS_OPEN')!,
          values: [
            panelInfo?.breakerInfo?.doubleBreakerAvailable
              ? DOUBLE_BREAKER_OPTIONS[
                  panelInfo?.breakerInfo?.doubleBreakerAvailable
                ]
              : '',
          ],
        },
        ...(panelInfo?.chargerCost && panelInfo?.receiptImage
          ? [
              {
                label: contentUploadPhotos.get('UPLOAD_CHARGER_RECEIPT_TITLE'),
                values: [
                  `$${toCurrencyString(panelInfo?.chargerCost, true)}`,
                  panelInfo?.receiptImage,
                ],
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <Box my={4}>
      <Box my={5}>
        <Typography variant={'h1'} css={{ textAlign: 'center' }}>
          {content.get('YOUR_SUBMITTED_EV_APPLICATION')}
        </Typography>
      </Box>

      <Box
        mt={2.5}
        css={{
          marginBottom: 42,
        }}
      >
        <Paper
          css={{ padding: '30px 25px', border: `1px solid ${colors.noirBlur}` }}
        >
          {summary.map((summaryItem, idx) => (
            <Fragment>
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant={'h4'}
                    css={{
                      wordWrap: 'break-word',
                      width: '85%',
                      fontSize: 20,
                      fontWeight: 'bold',
                      color: colors.midGrey,
                      marginBottom: 1,
                      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                        fontSize: 16,
                      },
                    }}
                  >
                    {summaryItem.sectionTitle}
                  </Typography>
                </Box>

                {summaryItem.items.map(item => (
                  <Box key={item.label} css={{ padding: '0px 18px 0 32px' }}>
                    <Typography
                      variant={'body1'}
                      css={{
                        fontSize: 20,
                        fontWeight: 'bold',
                        color: colors.noirBlur,
                        marginTop: '16px',
                        lineHeight: '26px',
                        [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                          fontSize: 16,
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                    {item.values.map(val => (
                      <Typography
                        variant="body1"
                        css={{
                          wordWrap: 'break-word',
                          fontSize: 20,
                          color: colors.noirBlur,
                          marginTop: 10,
                          '&:first-child': {
                            marginTop: 1,
                          },
                          [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                            fontSize: 16,
                          },
                        }}
                      >
                        {val}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>

              {idx !== summary.length - 1 && (
                <Divider
                  css={{
                    color: colors.gray93,
                    margin: theme.spacing(2, 0, 2, 0),
                  }}
                />
              )}
            </Fragment>
          ))}
        </Paper>
      </Box>

      <Box display={'flex'} justifyContent={'center'}>
        <PGEPlusButtons
          variant="outlined"
          css={{ minWidth: '10rem' }}
          onClick={() =>
            void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK, {
              state: { accountNumber: application?.accountId },
            })
          }
        >
          {content.get('REUSABLE_BUTTON_BACK')}
        </PGEPlusButtons>
      </Box>
    </Box>
  );
};
